import { useEffect, useRef, useState } from 'react';
import {
  getResultsReportStatus,
  ResultsReportJobStatus,
  ResultsReportStatus,
  ResultsReportStep,
} from 'api/bitedb/resultsReport';

const POLL_INTERVAL_MS = 2000;

export interface UseResultsReportJobResult {
  status: ResultsReportJobStatus | null;
  step: ResultsReportStep | null;
  processedEntities: number | null;
  totalEntities: number | null;
  percent: number | null;
  error: string | null;
  result: NonNullable<ResultsReportStatus['result']> | null;
  isPolling: boolean;
}

const isTerminal = (status: ResultsReportJobStatus | null): boolean =>
  status === 'completed' || status === 'failed';

export function useResultsReportJob(
  jobId: string | null,
): UseResultsReportJobResult {
  const [status, setStatus] = useState<ResultsReportJobStatus | null>(null);
  const [step, setStep] = useState<ResultsReportStep | null>(null);
  const [processedEntities, setProcessedEntities] = useState<number | null>(
    null,
  );
  const [totalEntities, setTotalEntities] = useState<number | null>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NonNullable<
    ResultsReportStatus['result']
  > | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const jobIdRef = useRef<string | null>(jobId);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    jobIdRef.current = jobId;

    // Reset state when jobId changes or is cleared
    setStatus(null);
    setStep(null);
    setProcessedEntities(null);
    setTotalEntities(null);
    setPercent(null);
    setError(null);
    setResult(null);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!jobId) {
      setIsPolling(false);
      return;
    }

    const applyStatus = (data: ResultsReportStatus) => {
      if (!isMountedRef.current) return;
      if (jobIdRef.current !== jobId) return;
      setStatus(data.status ?? null);
      setStep(data.step ?? null);
      setProcessedEntities(
        typeof data.processedEntities === 'number'
          ? data.processedEntities
          : null,
      );
      setTotalEntities(
        typeof data.totalEntities === 'number' ? data.totalEntities : null,
      );
      setPercent(typeof data.percent === 'number' ? data.percent : null);
      setError(data.error ?? null);
      setResult(data.result ?? null);

      if (isTerminal(data.status ?? null)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsPolling(false);
      }
    };

    const fetchOnce = async () => {
      try {
        const data = await getResultsReportStatus(jobId);
        applyStatus(data);
      } catch (err: any) {
        if (!isMountedRef.current) return;
        if (jobIdRef.current !== jobId) return;
        setError(err?.message || 'Failed to fetch job status');
      }
    };

    setIsPolling(true);
    fetchOnce();

    intervalRef.current = setInterval(() => {
      if (!isMountedRef.current || jobIdRef.current !== jobId) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }
      fetchOnce();
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [jobId]);

  return {
    status: status,
    step: step,
    processedEntities: processedEntities,
    totalEntities: totalEntities,
    percent: percent,
    error: error,
    result: result,
    isPolling: isPolling,
  };
}
